import { useState, useCallback } from 'react'
import {
  searchNSCustomersDebounced,
  transformNetSuiteCustomerForForm,
} from 'lib/netsuiteUtils'
import { SchoolLookUpSiteData } from 'types'

interface UseCustomerIdValidationOptions {
  onValidCustomer: (
    schoolData: SchoolLookUpSiteData,
    formik: any,
  ) => Promise<void>
  onClear?: (formik: any, currentSchool: SchoolLookUpSiteData) => Promise<void>
  currentSchool: SchoolLookUpSiteData
}

interface ValidationCache {
  customerId: string
  validation: string
}
/**
 * Hook for validating NetSuite Customer ID input
 */
export const useCustomerIdValidation = ({
  onValidCustomer,
  onClear,
  currentSchool,
}: UseCustomerIdValidationOptions) => {
  const [validationCache, setValidationCache] = useState<ValidationCache>({
    customerId: '',
    validation: '',
  })

  const validateAsync = useCallback(
    async (value: string, formik: any): Promise<string> => {
      const trimmedValue = value?.trim() || ''

      // Return cached result if value hasn't changed to avoid unnecessary API calls
      if (trimmedValue === validationCache.customerId) {
        return validationCache.validation
      }

      // Clear validation for empty input
      if (!trimmedValue) {
        setValidationCache({ customerId: '', validation: '' })
        // When NetSuite Customer ID is cleared, restore the dropdown selection if available
        if (onClear && currentSchool.netsuiteCustomerId) {
          await onClear(formik, currentSchool)
        }
        return ''
      }

      try {
        // Validate format - must be numeric
        if (!/^[0-9]+$/.test(trimmedValue)) {
          const errorMessage = 'Customer ID must contain only numbers'
          setValidationCache({
            customerId: trimmedValue,
            validation: errorMessage,
          })
          return errorMessage
        }

        // Search for the customer in NetSuite using debounced API
        const response = await searchNSCustomersDebounced(trimmedValue)
        const netsuiteCustomer = (response as any)?.data?.[0]

        if (netsuiteCustomer) {
          // Customer found - update form and clear validation
          setValidationCache({ customerId: trimmedValue, validation: '' })

          const schoolData = transformNetSuiteCustomerForForm(netsuiteCustomer)
          await onValidCustomer(schoolData, formik)

          return ''
        } else {
          // Customer not found
          const errorMessage = `Cannot find Customer ID ${trimmedValue}`
          setValidationCache({
            customerId: trimmedValue,
            validation: errorMessage,
          })
          return errorMessage
        }
      } catch (err: any) {
        // Handle API errors gracefully
        const errorMessage = err?.message || 'Error validating Customer ID'
        setValidationCache({
          customerId: trimmedValue,
          validation: errorMessage,
        })
        return errorMessage
      }
    },
    [validationCache, onValidCustomer, onClear, currentSchool],
  )

  return {
    validateAsync,
    validationCache,
  }
}
